import { useState, useEffect, useCallback, useMemo } from "react";
import { listSites, cannibalizationReport, type CannibalQuery, type FluxPage } from "../lib/authClient";
import { siteKey } from "../lib/siteData";
import { showAlert } from "../lib/dialog";

// Two Google Search Console analyses appended to the bottom of the Cannibalization tab:
//   1. Keyword Cannibalization — queries where ≥2 of the site's pages rank (last 90 days).
//   2. SERP Flickering — pages moving in/out of the results over the last 30 days.
// Read-only: it only reads GSC data (no WordPress changes), so it needs a connected GSC
// property but not a WordPress connection.

interface Props { siteUrl?: string; }

const RESULTS_KEY = "lsg_cannibal_results";

function urlPath(url: string): string { try { return new URL(url).pathname || "/"; } catch { return url; } }

// Deep link into the GSC performance report for one page: last 3 months (~90 days) with
// Clicks/Impressions/Avg Position selected, filtered to that page. (GSC's UI URL params are
// undocumented but stable; degrades to the property report if Google changes them.)
function gscPerformanceUrl(property: string, pageUrl: string): string {
  const q = [
    `resource_id=${encodeURIComponent(property)}`,
    "num_of_months=3",
    `metrics=${encodeURIComponent("CLICKS,IMPRESSIONS,POSITION")}`,
    `page=${encodeURIComponent("*" + pageUrl)}`,
  ].join("&");
  return `https://search.google.com/search-console/performance/search-analytics?${q}`;
}

type FluxSort = { col: "page" | "status"; dir: "asc" | "desc" };

interface StoredResults { siteKey: string; cannibalization: CannibalQuery[]; flux: FluxPage[]; }

const FLUX_TONE: Record<FluxPage["status"], { bg: string; bd: string; fg: string }> = {
  "Dropped Out":    { bg: "#fee2e2", bd: "#fecaca", fg: "#b91c1c" },
  "Newly Appeared": { bg: "#dcfce7", bd: "#bbf7d0", fg: "#166534" },
  "Intermittent":   { bg: "#ffedd5", bd: "#fed7aa", fg: "#9a3412" },
};

export function CannibalizationTools(props: Props) {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [gscConnected, setGscConnected] = useState(false);
  const [gscProperty, setGscProperty] = useState(""); // e.g. "sc-domain:example.com" — for GSC deep links
  const [fluxSort, setFluxSort] = useState<FluxSort | null>(null);
  const [crawling, setCrawling] = useState(false);
  const [status, setStatus] = useState<{ tone: "info" | "err"; text: string } | null>(null);
  const [cannibalization, setCannibalization] = useState<CannibalQuery[]>([]);
  const [flux, setFlux] = useState<FluxPage[]>([]);
  const [loaded, setLoaded] = useState(false);

  const key = props.siteUrl ? siteKey(props.siteUrl) : "";

  // Resolve the active site → its saved-site id + GSC connection status.
  useEffect(() => {
    let alive = true;
    if (!key) { setSiteId(null); setGscConnected(false); setGscProperty(""); return; }
    listSites().then(sites => {
      if (!alive) return;
      const match = sites.find(s => siteKey(s.siteUrl) === key);
      setSiteId(match?.id ?? null);
      setGscConnected(!!match?.gscConnected);
      setGscProperty(match?.gscProperty ?? "");
    }).catch(() => { if (alive) { setSiteId(null); setGscConnected(false); setGscProperty(""); } });
    return () => { alive = false; };
  }, [key]);

  // Restore the last analysis for THIS site (survives tab switches).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RESULTS_KEY);
      if (raw) {
        const s = JSON.parse(raw) as StoredResults;
        if (s.siteKey === key) { setCannibalization(s.cannibalization ?? []); setFlux(s.flux ?? []); setLoaded(true); return; }
      }
    } catch { /* ignore */ }
    setCannibalization([]); setFlux([]); setLoaded(false);
  }, [key]);

  const runCrawl = useCallback(async () => {
    if (crawling) return;
    if (!key) { void showAlert("Select a website first (top of the page).", { title: "No Website Selected" }); return; }
    setCrawling(true);
    setStatus({ tone: "info", text: "Verifying the Google Search Console connection…" });

    // Validate the GSC connection live (the mount-time flag can be stale).
    let liveSiteId: string;
    try {
      const sites = await listSites();
      const match = sites.find(s => siteKey(s.siteUrl) === key);
      setSiteId(match?.id ?? null);
      setGscConnected(!!match?.gscConnected);
      setGscProperty(match?.gscProperty ?? "");
      if (!match || !match.id || !match.gscConnected) {
        setStatus(null); setCrawling(false);
        void showAlert("Connect this website to Google Search Console first — open Settings → Saved Websites and connect a property.", { title: "Google Search Console Not Connected" });
        return;
      }
      liveSiteId = match.id;
    } catch {
      setStatus(null); setCrawling(false);
      void showAlert("Couldn't verify the Google Search Console connection. Please try again.", { title: "Connection Check Failed" });
      return;
    }

    setStatus({ tone: "info", text: "Analyzing Search Console data — cannibalization (90 days) and SERP flux (30 days)…" });
    try {
      const { cannibalization: c, flux: f } = await cannibalizationReport(liveSiteId);
      setCannibalization(c); setFlux(f); setLoaded(true);
      try { localStorage.setItem(RESULTS_KEY, JSON.stringify({ siteKey: key, cannibalization: c, flux: f } as StoredResults)); } catch { /* ignore */ }
      setStatus({ tone: "info", text: `Found ${c.length} cannibalized keyword${c.length === 1 ? "" : "s"} and ${f.length} flickering page${f.length === 1 ? "" : "s"}.` });
    } catch (e) {
      setStatus({ tone: "err", text: e instanceof Error ? e.message : "Analysis failed." });
    } finally {
      setCrawling(false);
    }
  }, [crawling, key]);

  const notReady = !siteId || !gscConnected;
  // Title → white in dark mode (orange in light). Read the theme on render; App re-renders on toggle.
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const brandTitle: React.CSSProperties = { fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontSize: 20, fontWeight: 500, textTransform: "uppercase", color: isDark ? "#ffffff" : "#f97316", letterSpacing: ".7px" };
  const help: React.CSSProperties = { fontFamily: "'Inter','Mulish',sans-serif", fontSize: 12, color: isDark ? "#93a4c0" : "#64748b", marginTop: 4, lineHeight: 1.5, maxWidth: 720 };
  const num = (n: number) => n.toLocaleString();

  // Sortable flux table (Page A→Z / Status). No sort chosen → server order (impressions desc).
  const sortedFlux = useMemo(() => {
    if (!fluxSort) return flux;
    const dir = fluxSort.dir === "asc" ? 1 : -1;
    return [...flux].sort((a, b) =>
      (fluxSort.col === "page" ? urlPath(a.page).localeCompare(urlPath(b.page)) : a.status.localeCompare(b.status)) * dir);
  }, [flux, fluxSort]);
  const toggleSort = (col: FluxSort["col"]) =>
    setFluxSort(prev => (prev && prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }));
  const sortArrow = (col: FluxSort["col"]) => (fluxSort?.col === col ? (fluxSort.dir === "asc" ? " ▲" : " ▼") : "");

  return (
    <div style={{ marginTop: 40, borderTop: `2px solid ${isDark ? "#2f3a4d" : "#e2e8f0"}`, paddingTop: 26 }}>
      <div style={{ padding: "0 9px", marginBottom: 14 }}>
        <div style={brandTitle}>Search Console Cannibalization &amp; SERP Flux</div>
        <div style={help}>
          Crawls this website&rsquo;s connected Google Search Console property to surface two issues:
          <strong> keyword cannibalization</strong> — search terms where more than one of your pages ranks, competing with each other (last 90 days) —
          and <strong> SERP flickering</strong> — pages that keep dropping out and reappearing in the results over the last 30 days.
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 12 }}>
          {notReady && <span style={{ fontSize: 12.5, fontWeight: 600, color: "#2563eb" }}>Connect Google Search Console to run this analysis.</span>}
          <button
            className="run-crawl-btn"
            onClick={() => void runCrawl()}
            disabled={crawling || notReady}
            style={{
              padding: "10px 18px", border: "none", borderRadius: 0, fontFamily: "'Inter','Mulish',sans-serif",
              fontSize: 13, fontWeight: 700, color: "#fff", cursor: crawling || notReady ? "default" : "pointer",
              background: crawling || notReady ? "#fdba74" : "linear-gradient(135deg,#ea580c,#f97316)", whiteSpace: "nowrap",
              transition: "background .3s ease, box-shadow .15s, transform .15s",
            }}
          >
            {crawling ? "Analyzing…" : "Run Crawl"}
          </button>
        </div>
      </div>

      {status && (
        <div style={{ padding: "0 9px", marginBottom: 16, fontSize: 12.5, color: status.tone === "err" ? (isDark ? "#f87171" : "#dc2626") : (isDark ? "#93a4c0" : "#64748b"), display: "flex", alignItems: "center", gap: 7 }}>
          {crawling && <span className="lsg-spinner" />}{status.text}
        </div>
      )}

      {/* ── Section 1: Keyword Cannibalization ── */}
      <div style={{ padding: "0 9px", marginBottom: 26 }}>
        <div className="cd-section">
          <div className="cd-section-hd"><span className="cd-hd-brand">Keyword Cannibalization</span></div>
          <p className="cd-help">Search queries where two or more of your pages rank — they compete with each other, splitting clicks and confusing Google about which page to rank. Consider consolidating or differentiating the competing pages.</p>
          {!loaded ? (
            <p className="cd-help">Run the crawl above to analyze the last 90 days of Search Console data.</p>
          ) : cannibalization.length === 0 ? (
            <p className="cd-help">No cannibalized keywords found — no query has more than one of your pages ranking with meaningful impressions.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {cannibalization.map(q => (
                <div key={q.query} style={{ border: `1px solid ${isDark ? "#2f3a4d" : "#e2e8f0"}`, borderRadius: 8, background: isDark ? "#16223a" : "#fff", overflow: "hidden" }}>
                  <div style={{ background: isDark ? "rgba(34,197,94,.14)" : "#def5e7", borderBottom: `1px solid ${isDark ? "rgba(255,255,255,.10)" : "#c7ecd6"}`, padding: "9px 13px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: isDark ? "#e6edf6" : "#0f172a" }}>{q.query}</span>
                    <span style={{ fontSize: 11, color: isDark ? "#86efac" : "#166534", fontWeight: 600, whiteSpace: "nowrap" }}>{q.pages.length} pages · {num(q.totalImpressions)} impressions · {num(q.totalClicks)} clicks</span>
                  </div>
                  <table className="cd-history" style={{ margin: 0, tableLayout: "fixed", width: "100%" }}>
                    <colgroup><col /><col style={{ width: 120 }} /><col style={{ width: 120 }} /><col style={{ width: 100 }} /></colgroup>
                    <thead><tr><th>Competing Page</th><th>Avg Position</th><th>Impressions</th><th>Clicks</th></tr></thead>
                    <tbody>
                      {q.pages.map(pg => (
                        <tr key={pg.page}>
                          <td><a href={pg.page} target="_blank" rel="noopener noreferrer">{urlPath(pg.page)}</a></td>
                          <td style={{ whiteSpace: "nowrap", color: "#ea580c", fontWeight: 700 }}>{pg.position ?? "—"}</td>
                          <td style={{ whiteSpace: "nowrap" }}>{num(pg.impressions)}</td>
                          <td style={{ whiteSpace: "nowrap" }}>{num(pg.clicks)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Section 2: SERP Flickering ── */}
      <div style={{ padding: "0 9px" }}>
        <div className="cd-section">
          <div className="cd-section-hd"><span className="cd-hd-brand">SERP Flickering — Last 30 Days</span></div>
          <p className="cd-help">Pages whose Search Console visibility is unstable over the last 30 days — dropping out of the results and reappearing, rather than ranking steadily. Flickering often signals thin/duplicate content, indexing issues, or volatile rankings worth investigating.</p>
          {!loaded ? (
            <p className="cd-help">Run the crawl above to analyze the last 30 days of Search Console data.</p>
          ) : flux.length === 0 ? (
            <p className="cd-help">No flickering pages found — your pages held steady visibility over the last 30 days.</p>
          ) : (
            <table className="cd-history">
              <thead><tr>
                <th onClick={() => toggleSort("page")} style={{ cursor: "pointer", userSelect: "none" }} title="Sort by page (A–Z)">Page{sortArrow("page")}</th>
                <th onClick={() => toggleSort("status")} style={{ cursor: "pointer", userSelect: "none" }} title="Sort by status">Status{sortArrow("status")}</th>
                <th>Days Visible</th><th>Impressions</th><th>Avg Position</th>
              </tr></thead>
              <tbody>
                {sortedFlux.map(p => {
                  const tone = FLUX_TONE[p.status];
                  return (
                    <tr key={p.page}>
                      <td>
                        {gscProperty
                          ? <a href={gscPerformanceUrl(gscProperty, p.page)} target="_blank" rel="noopener noreferrer" title="Open this page's last-90-days performance in Google Search Console">{urlPath(p.page)}</a>
                          : <a href={p.page} target="_blank" rel="noopener noreferrer">{urlPath(p.page)}</a>}
                      </td>
                      <td><span style={{ fontSize: 10.5, fontWeight: 700, color: tone.fg, background: tone.bg, border: `1px solid ${tone.bd}`, borderRadius: 10, padding: "2px 9px", whiteSpace: "nowrap" }}>{p.status}</span></td>
                      <td style={{ whiteSpace: "nowrap" }}>{p.daysVisible} / 30</td>
                      <td style={{ whiteSpace: "nowrap" }}>{num(p.impressions)}</td>
                      <td style={{ whiteSpace: "nowrap", color: "#ea580c", fontWeight: 700 }}>{p.avgPosition ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
